import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Copy, Edit3, Check, X, Info } from "lucide-react";

/**
 * Manage the list of programs (dropdown options) for a single campus.
 * The list is the source of truth for both:
 *   - Program names used in email template lookups (exact match against lead.group)
 *   - Program names that MUST be pasted into the WordPress CF7 <select> options
 */
export default function ProgramsManagerDialog({ open, onClose, campus, campusLabel, onChanged }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const load = async () => {
    if (!campus) return;
    setLoading(true);
    try {
      const r = await api.get(`/campus-programs?campus=${encodeURIComponent(campus)}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campus]);

  const add = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      await api.post("/campus-programs", {
        campus,
        name: trimmed,
        order: items.length + 1,
      });
      setNewName("");
      await load();
      onChanged?.();
      toast.success(t("program_added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const saveEdit = async () => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    try {
      await api.patch(`/campus-programs/${editingId}`, { name: trimmed });
      setEditingId(null);
      setEditingName("");
      await load();
      onChanged?.();
      toast.success(t("program_renamed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title: "¿Borrar programa?",
      description: `Se eliminará "${item.name}" del catálogo de ${campusLabel || campus}. No se puede deshacer.`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/campus-programs/${item.id}`);
      await load();
      onChanged?.();
      toast.success(t("program_deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const copyList = async () => {
    const text = items.map((i) => i.name).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("program_list_copied"));
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const copyCf7Shortcode = async () => {
    // Contact Form 7 select syntax: [select* menu-123 include_blank "Option 1" "Option 2"]
    // We just build the quoted options part so the user can paste it into their
    // existing [select* menu-XXX ...] shortcode.
    const options = items.map((i) => `"${i.name.replace(/"/g, "'")}"`).join(" ");
    const shortcode = `[select* menu-100 include_blank ${options}]`;
    try {
      await navigator.clipboard.writeText(shortcode);
      toast.success(t("program_cf7_copied"));
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-xl" data-testid="programs-manager-dialog">
        <DialogHeader>
          <DialogTitle>{t("programs_manager_title").replace("{campus}", campusLabel || campus)}</DialogTitle>
          <DialogDescription>
            {t("programs_manager_desc")}
          </DialogDescription>
        </DialogHeader>

        {/* Info banner */}
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-700 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 leading-relaxed">
            {t("programs_manager_hint")}
          </div>
        </div>

        {/* Add new */}
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("program_add_placeholder")}
            className="flex-1"
            data-testid="program-new-input"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
          <Button
            onClick={add}
            disabled={adding || !newName.trim()}
            data-testid="program-add-btn"
            className="btn-brand"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t("add")}
          </Button>
        </div>

        {/* List */}
        <div className="max-h-[300px] overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 flex items-center justify-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400 italic">
              {t("programs_empty_state")}
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="p-2.5 flex items-center gap-2" data-testid={`program-row-${item.id}`}>
                {editingId === item.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 h-8 text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                      }}
                    />
                    <Button size="icon" variant="ghost" onClick={saveEdit} className="h-8 w-8">
                      <Check className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditingId(null); setEditingName(""); }} className="h-8 w-8">
                      <X className="h-4 w-4 text-slate-500" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-slate-800 truncate">{item.name}</span>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(item)} className="h-8 w-8" data-testid={`program-edit-${item.id}`}>
                      <Edit3 className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(item)} className="h-8 w-8" data-testid={`program-del-${item.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={copyList}
            disabled={items.length === 0}
            data-testid="programs-copy-list-btn"
          >
            <Copy className="h-4 w-4" />
            {t("program_copy_list")}
          </Button>
          <Button
            variant="outline"
            onClick={copyCf7Shortcode}
            disabled={items.length === 0}
            data-testid="programs-copy-cf7-btn"
          >
            <Copy className="h-4 w-4" />
            {t("program_copy_cf7")}
          </Button>
          <Button onClick={onClose} data-testid="programs-close-btn">
            {t("done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
